import { formatRecordedDate, recordedBooleanLabel } from '@/utils/recordedValues'
/**
 * User Detail Page
 * Displays comprehensive user information, activity, and management options
 * Accessible to Owner and Admin roles
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  User,
  Mail,
  Phone,
  Shield,
  Building2,
  Calendar,
  Clock,
  ChevronLeft,
  Edit2,
  UserX,
  UserCheck,
  KeyRound,
  Send,
  Monitor,
  MapPin,
  Activity,
  MoreVertical,
  Trash2,
  AlertTriangle,
  Sliders,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useUserStore } from '@/store/userStore'
import { toast } from '@/store/toastStore'
import { useSessionStore } from '@/store/sessionStore'
import { useAuditStore } from '@/store/auditStore'
import { getRoleDisplayName, getRoleBadgeClass } from '@/types/auth.types'
import { formatRelativeTime } from '@/types/session.types'
import {
  RoleAssignmentModal,
  UserModal,
  UserStatusModal,
  PermissionOverridesModal,
} from '@/components/users'
import type { UserFormData } from '@/components/users'
import type { UserRole, UserPermissionOverrides } from '@/types/user.types'
import { getAssignableRoles, ROLE_PERMISSIONS_SUMMARY } from '@/types/user.types'

// ============================================================================
// ACTIVITY ITEM COMPONENT
// ============================================================================

function ActivityItem({
  icon: Icon,
  title,
  description,
  time,
  iconColor = 'text-primary-500',
}: {
  icon: React.ElementType
  title: string
  description?: string
  time: string
  iconColor?: string
}) {
  return (
    <div className="flex gap-3 py-3">
      <div
        className={`w-8 h-8 rounded-full bg-surface-overlay flex items-center justify-center flex-shrink-0`}
      >
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary">{title}</p>
        {description && <p className="text-xs text-text-tertiary mt-0.5">{description}</p>}
      </div>
      <span className="text-xs text-text-tertiary whitespace-nowrap">{time}</span>
    </div>
  )
}

// ============================================================================
// INFO ROW COMPONENT
// ============================================================================

function InfoRow({
  icon: Icon,
  label,
  value,
  badge,
}: {
  icon: React.ElementType
  label: string
  value: string | React.ReactNode
  badge?: { text: string; className: string }
}) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-surface-overlay last:border-0">
      <Icon className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-text-tertiary">{label}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {typeof value === 'string' ? (
            <p className="text-sm font-medium text-text-primary">{value}</p>
          ) : (
            value
          )}
          {badge && <span className={`badge badge-sm ${badge.className}`}>{badge.text}</span>}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user: currentUser, hasPermission } = useAuth()
  const canCreate = hasPermission('txh.user.create')
  const canUpdate = hasPermission('txh.user.update')
  const canDelete = hasPermission('txh.user.delete')

  // Stores
  const {
    users,
    isLoading,
    loadUsers,
    updateUser,
    deleteUser,
    activateUser,
    deactivateUser,
    assignRole,
  } = useUserStore()
  const { sessions, loadUserSessions } = useSessionStore()
  const { entries: auditEntries, loadUserAuditLog } = useAuditStore()

  // Local state
  const [showEditModal, setShowEditModal] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showOverridesModal, setShowOverridesModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const [isLoadingDetails, setIsLoadingDetails] = useState(true)

  // Find the user
  const user = users.find(u => u.userId === userId)

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingDetails(true)
      if (currentUser?.tenantId) {
        await loadUsers(currentUser.tenantId)
      }
      if (userId) {
        await Promise.all([loadUserSessions(userId), loadUserAuditLog(userId)])
      }
      setIsLoadingDetails(false)
    }
    loadData()
  }, [currentUser?.tenantId, userId, loadUsers, loadUserSessions, loadUserAuditLog])

  // User sessions
  const userSessions = sessions.filter(s => s.userId === userId)
  const activeSessions = userSessions.filter(s => s.status === 'active')

  // User audit entries (recent 10)
  const userAuditEntries = auditEntries.filter(e => e.userId === userId).slice(0, 10)

  // Available roles for assignment
  const availableRoles = currentUser ? getAssignableRoles(currentUser.role) : []

  // Handlers
  const handleEditUser = async (data: UserFormData) => {
    if (!canUpdate || !user) return
    try {
      await updateUser(user.userId, {
        fullName: data.fullName,
        phone: data.phone,
        isActive: data.isActive,
      })
      toast.success('Changes saved', `${data.fullName}'s profile was updated.`)
    } catch {
      toast.error('Could not save changes', 'Please try again.')
    }
    setShowEditModal(false)
  }

  const handleRoleChange = async (targetUserId: string, newRole: UserRole) => {
    if (!canUpdate) return
    try {
      await assignRole(targetUserId, newRole)
      toast.success('Role updated', `The user's role is now ${newRole}.`)
    } catch {
      toast.error('Could not update role', 'Please try again.')
    }
    setShowRoleModal(false)
  }

  const handleToggleStatus = () => {
    if (!canUpdate || !user) return
    setShowStatusModal(true)
  }

  const confirmStatusChange = async () => {
    if (!canUpdate || !user) return
    const isDeactivating = user.isActive
    try {
      if (isDeactivating) {
        await deactivateUser(user.userId)
        toast.success('User deactivated', `${user.fullName} has been deactivated and signed out.`)
      } else {
        await activateUser(user.userId)
        toast.success('User activated', `${user.fullName}'s access has been restored.`)
      }
    } catch (err) {
      toast.error(
        `Could not ${isDeactivating ? 'deactivate' : 'activate'} user`,
        err instanceof Error ? err.message : 'Please try again.'
      )
      throw err
    }
  }

  const handleSaveOverrides = async (overrides: UserPermissionOverrides) => {
    if (!canUpdate || !user) return
    try {
      await updateUser(user.userId, {
        preferences: {
          ...user.preferences,
          permissionOverrides: overrides,
        },
      })
      toast.success('Permissions updated', `Permission overrides saved for ${user.fullName}.`)
    } catch (err) {
      toast.error(
        'Could not save permissions',
        err instanceof Error ? err.message : 'Please try again.'
      )
      throw err
    }
  }

  const handleDeleteUser = async () => {
    if (!canDelete || !user) return
    if (
      window.confirm(
        `Are you sure you want to delete ${user.fullName}? This action cannot be undone.`
      )
    ) {
      try {
        await deleteUser(user.userId)
        toast.success('User deleted', `${user.fullName} has been removed.`)
        navigate('/users')
      } catch {
        toast.error('Could not delete user', 'Please try again.')
      }
    }
  }

  const handleResendInvite = () => {
    if (!canCreate || !user) return
    toast.success('Invitation resent', `A new invite was sent to ${user.email}.`)
  }

  // Loading state
  if (isLoading || isLoadingDetails) {
    return (
      <div className="p-6 space-y-6 max-w-6xl mx-auto">
        <div className="skeleton h-4 w-28 rounded" />
        <div className="card card-elevated p-6">
          <div className="flex items-center gap-4">
            <div className="skeleton w-16 h-16 rounded-full" />
            <div className="space-y-2">
              <div className="skeleton h-6 w-48 rounded" />
              <div className="skeleton h-4 w-56 rounded" />
              <div className="skeleton h-5 w-24 rounded-full" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="card card-elevated p-6 space-y-3">
              <div className="skeleton h-5 w-40 rounded" />
              <div className="skeleton h-4 w-full rounded" />
              <div className="skeleton h-4 w-3/4 rounded" />
              <div className="skeleton h-4 w-2/3 rounded" />
            </div>
            <div className="card card-elevated p-6 space-y-3">
              <div className="skeleton h-5 w-40 rounded" />
              <div className="skeleton h-4 w-1/2 rounded" />
              <div className="skeleton h-4 w-1/3 rounded" />
            </div>
          </div>
          <div className="space-y-6">
            <div className="card card-elevated p-6 space-y-3">
              <div className="skeleton h-5 w-32 rounded" />
              <div className="skeleton h-4 w-full rounded" />
              <div className="skeleton h-4 w-2/3 rounded" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  // User not found
  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary">User Not Found</h2>
        <p className="text-text-secondary">
          The user you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/users" className="btn btn-primary gap-2">
          <ChevronLeft className="w-4 h-4" />
          Back to Users
        </Link>
      </div>
    )
  }

  // Get initials for avatar
  const initials = user.fullName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  // Check if this is the current user
  const isCurrentUser = currentUser?.id === user.userId

  // Check if user can be managed
  const canManageThisUser = !isCurrentUser && currentUser && availableRoles.includes(user.role)

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Back Link */}
      <Link
        to="/users"
        className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-primary-600 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Users
      </Link>

      {/* Header Card */}
      <div className="card card-elevated p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold ${
                user.isActive ? 'bg-gradient-to-br from-primary-500 to-primary-600' : 'bg-gray-400'
              }`}
            >
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.fullName}
                  className="w-full h-full rounded-full"
                />
              ) : (
                initials
              )}
            </div>

            {/* Name & Status */}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-text-primary">{user.fullName}</h1>
                {isCurrentUser && <span className="badge badge-primary badge-sm">You</span>}
              </div>
              <p className="text-text-secondary">{user.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                  {getRoleDisplayName(user.role)}
                </span>
                {user.isVerified === true ? (
                  <span className="badge badge-success badge-sm">Verified</span>
                ) : user.isVerified === false ? (
                  <span className="badge badge-warning badge-sm">Pending</span>
                ) : (
                  <span className="badge badge-ghost badge-sm">Not recorded</span>
                )}
                {user.isActive === false && (
                  <span className="badge badge-error badge-sm">Inactive</span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <>
            {canManageThisUser && (canCreate || canUpdate || canDelete) && (
              <div className="flex items-center gap-2">
                {canUpdate && (
                  <button
                    type="button"
                    onClick={() => setShowEditModal(true)}
                    className="btn btn-outline btn-sm gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                )}
                {canUpdate && (
                  <button
                    type="button"
                    onClick={() => setShowRoleModal(true)}
                    className="btn btn-outline btn-sm gap-2"
                  >
                    <Shield className="w-4 h-4" />
                    Change Role
                  </button>
                )}
                {(canUpdate || canDelete || (user.isVerified === false && canCreate)) && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowActionsMenu(!showActionsMenu)}
                      className="btn btn-ghost btn-sm btn-square"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {showActionsMenu && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setShowActionsMenu(false)}
                        />
                        <div className="absolute right-0 top-full mt-1 w-48 bg-surface-light dark:bg-surface-dark border border-surface-overlay rounded-xl shadow-xl z-50 py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                          {user.isVerified === false && canCreate && (
                            <button
                              type="button"
                              onClick={() => {
                                handleResendInvite()
                                setShowActionsMenu(false)
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-overlay flex items-center gap-2"
                            >
                              <Send className="w-4 h-4" />
                              Resend Invite
                            </button>
                          )}
                          {canUpdate && (
                            <button
                              type="button"
                              onClick={() => {
                                handleToggleStatus()
                                setShowActionsMenu(false)
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-overlay flex items-center gap-2"
                            >
                              {user.isActive ? (
                                <>
                                  <UserX className="w-4 h-4" />
                                  Deactivate User
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-4 h-4" />
                                  Activate User
                                </>
                              )}
                            </button>
                          )}
                          {canDelete && (
                            <>
                              <hr className="my-1 border-surface-overlay" />
                              <button
                                type="button"
                                onClick={() => {
                                  handleDeleteUser()
                                  setShowActionsMenu(false)
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete User
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - User Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="card card-elevated p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Personal Information</h2>
            <div className="divide-y divide-surface-overlay">
              <InfoRow icon={User} label="Full Name" value={user.fullName} />
              <InfoRow icon={Mail} label="Email Address" value={user.email} />
              <InfoRow icon={Phone} label="Phone Number" value={user.phone || 'Not provided'} />
              <InfoRow icon={Building2} label="Organization" value={user.tenantName || 'Unknown'} />
              <InfoRow
                icon={Shield}
                label="Role"
                value={
                  <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                    {getRoleDisplayName(user.role)}
                  </span>
                }
              />
            </div>
          </div>

          {/* Role & Granular Permissions */}
          <div className="card card-elevated p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">
                  Role & Granular Permissions
                </h2>
                <p className="text-xs text-text-tertiary">
                  Base permissions inherited from role and customized permission overrides
                </p>
              </div>
              {canUpdate && canManageThisUser && (
                <button
                  type="button"
                  onClick={() => setShowOverridesModal(true)}
                  className="btn btn-outline btn-xs gap-1.5"
                >
                  <Sliders className="w-3.5 h-3.5" />
                  Configure Overrides
                </button>
              )}
            </div>

            {/* Base role permissions */}
            <div className="space-y-2 mb-4">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Inherited from {getRoleDisplayName(user.role)}
              </p>
              <div className="space-y-1.5">
                {ROLE_PERMISSIONS_SUMMARY[user.role]?.map((permission, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                    <span className="text-text-secondary">{permission}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Granular Overrides */}
            {(user.preferences?.permissionOverrides?.granted?.length ?? 0) > 0 ||
            (user.preferences?.permissionOverrides?.revoked?.length ?? 0) > 0 ? (
              <div className="pt-3 border-t border-surface-overlay space-y-3">
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  Active Overrides
                </p>
                {user.preferences?.permissionOverrides?.granted &&
                  user.preferences.permissionOverrides.granted.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                        + Granted Capabilities (
                        {user.preferences.permissionOverrides.granted.length}):
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {user.preferences.permissionOverrides.granted.map(perm => (
                          <span key={perm} className="badge badge-success badge-xs font-mono">
                            +{perm}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                {user.preferences?.permissionOverrides?.revoked &&
                  user.preferences.permissionOverrides.revoked.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-[11px] font-medium text-red-600 dark:text-red-400">
                        - Restricted Capabilities (
                        {user.preferences.permissionOverrides.revoked.length}):
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {user.preferences.permissionOverrides.revoked.map(perm => (
                          <span
                            key={perm}
                            className="badge badge-error badge-xs font-mono line-through"
                          >
                            -{perm}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            ) : (
              <div className="pt-3 border-t border-surface-overlay text-xs text-text-tertiary">
                No custom overrides configured. User operates with standard role privileges.
              </div>
            )}
          </div>

          {/* Activity History */}
          <div className="card card-elevated p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-text-primary">Recent Activity</h2>
              <Link
                to={`/audit-log?userId=${user.userId}`}
                className="text-sm text-primary-600 hover:underline"
              >
                View All
              </Link>
            </div>
            {userAuditEntries.length > 0 ? (
              <div className="divide-y divide-surface-overlay">
                {userAuditEntries.map(entry => (
                  <ActivityItem
                    key={entry.id}
                    icon={Activity}
                    title={entry.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    description={
                      entry.details ? JSON.stringify(entry.details).slice(0, 50) : undefined
                    }
                    time={formatRelativeTime(entry.createdAt)}
                    iconColor={
                      entry.success === true
                        ? 'text-green-500'
                        : entry.success === false
                          ? 'text-red-500'
                          : 'text-text-tertiary'
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="text-center text-text-tertiary py-4">No recent activity</p>
            )}
          </div>
        </div>

        {/* Right Column - Sessions & Status */}
        <div className="space-y-6">
          {/* Account Status */}
          <div className="card card-elevated p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Account Status</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Account Status</span>
                <span
                  className={`badge ${user.isActive === true ? 'badge-success' : user.isActive === false ? 'badge-error' : 'badge-ghost'}`}
                >
                  {recordedBooleanLabel(user.isActive, 'Active', 'Inactive')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Verification</span>
                <span
                  className={`badge ${user.isVerified === true ? 'badge-success' : user.isVerified === false ? 'badge-warning' : 'badge-ghost'}`}
                >
                  {recordedBooleanLabel(user.isVerified, 'Verified', 'Pending')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Active Sessions</span>
                <span className="badge badge-ghost">{activeSessions.length}</span>
              </div>
            </div>
          </div>

          {/* Important Dates */}
          <div className="card card-elevated p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Important Dates</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-primary-500 mt-0.5" />
                <div>
                  <p className="text-xs text-text-tertiary">Created</p>
                  <p className="text-sm font-medium text-text-primary">
                    {formatRecordedDate(user.createdAt, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
              {user.lastLoginAt && (
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-primary-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-text-tertiary">Last Login</p>
                    <p className="text-sm font-medium text-text-primary">
                      {formatRelativeTime(user.lastLoginAt)}
                    </p>
                  </div>
                </div>
              )}
              {user.invitedAt && (
                <div className="flex items-start gap-3">
                  <Send className="w-5 h-5 text-primary-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-text-tertiary">Invited</p>
                    <p className="text-sm font-medium text-text-primary">
                      {formatRelativeTime(user.invitedAt)}
                    </p>
                    {user.invitedByName && (
                      <p className="text-xs text-text-tertiary">by {user.invitedByName}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Active Sessions */}
          <div className="card card-elevated p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-text-primary">Active Sessions</h2>
              <span className="badge badge-ghost badge-sm">{activeSessions.length}</span>
            </div>
            {activeSessions.length > 0 ? (
              <div className="space-y-3">
                {activeSessions.slice(0, 3).map(session => (
                  <div
                    key={session.sessionId}
                    className="p-3 rounded-lg border border-surface-overlay"
                  >
                    <div className="flex items-start gap-3">
                      <Monitor className="w-5 h-5 text-text-secondary mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {session.browser || 'Unknown'} on {session.os || 'Unknown'}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-text-tertiary mt-1">
                          {session.city && (
                            <>
                              <MapPin className="w-3 h-3" />
                              <span>{session.city}</span>
                              <span>•</span>
                            </>
                          )}
                          <span>{formatRelativeTime(session.lastActivityAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {activeSessions.length > 3 && (
                  <Link
                    to={`/sessions?userId=${user.userId}`}
                    className="block text-center text-sm text-primary-600 hover:underline py-2"
                  >
                    View all {activeSessions.length} sessions
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-center text-text-tertiary py-4 text-sm">No active sessions</p>
            )}
          </div>

          {/* Quick Actions */}
          <>
            {canManageThisUser && (canCreate || canUpdate) && (
              <div className="card card-elevated p-6">
                <h2 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  {user.isVerified === false && canCreate && (
                    <button
                      type="button"
                      onClick={handleResendInvite}
                      className="w-full btn btn-outline btn-sm justify-start gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Resend Invitation
                    </button>
                  )}
                  {canUpdate && (
                    <button
                      type="button"
                      onClick={() =>
                        toast.success(
                          'Password reset sent',
                          `A reset email was sent to ${user.email}.`
                        )
                      }
                      className="w-full btn btn-outline btn-sm justify-start gap-2"
                    >
                      <KeyRound className="w-4 h-4" />
                      Reset Password
                    </button>
                  )}
                  {canUpdate && (
                    <button
                      type="button"
                      onClick={handleToggleStatus}
                      className={`w-full btn btn-sm justify-start gap-2 ${
                        user.isActive ? 'btn-warning' : 'btn-success'
                      }`}
                    >
                      {user.isActive ? (
                        <>
                          <UserX className="w-4 h-4" />
                          Deactivate Account
                        </>
                      ) : (
                        <>
                          <UserCheck className="w-4 h-4" />
                          Activate Account
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        </div>
      </div>

      {/* Edit User Modal */}
      {canUpdate && (
        <UserModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleEditUser}
          user={user}
          mode="edit"
          availableRoles={availableRoles}
        />
      )}

      {/* Role Assignment Modal */}
      {canUpdate && (
        <RoleAssignmentModal
          isOpen={showRoleModal}
          onClose={() => setShowRoleModal(false)}
          onSubmit={handleRoleChange}
          user={user}
          availableRoles={availableRoles}
          currentUserRole={currentUser?.role}
        />
      )}

      {/* User Status Modal (Activate/Deactivate) */}
      {showStatusModal && (
        <UserStatusModal
          isOpen={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          onConfirm={confirmStatusChange}
          user={user}
          action={user.isActive ? 'deactivate' : 'activate'}
        />
      )}

      {/* Permission Overrides Modal */}
      {showOverridesModal && (
        <PermissionOverridesModal
          isOpen={showOverridesModal}
          onClose={() => setShowOverridesModal(false)}
          onSave={handleSaveOverrides}
          user={user}
        />
      )}
    </div>
  )
}
